import { FC, MouseEvent } from "react";
import { styled } from "styled-components";
import { X, PointerLeft } from "@fenderdigital/ui/Icons/";
import { ContentfulNavigationItem } from "../lib/contentfulTypes";
import { buttonReset, colors, font } from "../lib/sharedStyles";
import MobileMenuLink from "./MobileMenuLink";
import MobileSystemNav from "./MobileSystemNav";

// Strip any HTML tags and return plain text
function stripHTML(input: string): string {
  const div = document.createElement("div");
  div.innerHTML = input;
  return div.textContent?.trim() || "";
}

const Drawer = styled.div<{ $brand: string; $isOpen: boolean; }>`
  position: fixed;
  z-index: 15;
  top: 0;
  width: 100%;
  height: 100vh;
  background-color: #fff;
  transform: translateX(-100%);
  transition: transform 250ms ease;
  overflow: scroll;

  ${({ $isOpen }) => $isOpen && `
    transform: translateX(0);
  `}

  // Dawn breakpoint
  @media (min-width: 1150px) {
    display: none;
  }

  ${({ $brand }) => $brand == 'jackson' && `
    background-color: #020202;
  `}
  
  img {
    max-width: 100%;
  }

  ${({ $brand }) => $brand == 'fender' && `
    max-width: 375px;
  `}
`;

const Menu = styled.ul<{ $brand: string; }>`
  padding: 0;
  list-style: none;
  margin: 0 0 16px;

  ${({ $brand }) => $brand == 'jackson' && `
    margin: 16px 0;
    padding: 0 42px;
  `}
`;

const DrawerHeader = styled.div<{ $brand: string; $level: number; }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 24px 16px 16px 24px;

  ${({ $brand, $level }) => $brand == 'fender' && $level > 1 && `
    padding-top: 35px;
    padding-bottom: 8px;
    border-bottom: 1px solid #E5E5E5;
  `}
`;

// Need to beat specificity of Drawer img above.
const LogoLink = styled.a<{ $brand: string; }>`
  > img {
    max-width: ${({ $brand }) => $brand == 'fender' ? '75px' : '120px'};
    vertical-align: bottom;
  }
`;

const BackButton = styled.button`
  ${buttonReset()}
  display: flex;
  align-items: center;
  ${font('Palanquin Dark', 500, '18px', 'normal', '0.07812rem')}
  color: #fff;
  text-transform: uppercase;
  
  span {
    width: 44px;
    height: 44px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding-top: 4px;
  }
`;

const DrawerTitle = styled.div`
  ${font('Instrument Sans', 600, '14px', '20px', '0.14px')};
  color: #000;
`

const CloseButton = styled.button`
  ${buttonReset()}
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const SubMenuHeading = styled.p`
  ${font('Palanquin Dark', 500, '18px', 'normal', '0.07812rem')}
  color: #fff;
  text-transform: uppercase;
  margin: 0;
  padding: 12px 42px;
`;

const SupportNav = styled.div`
  margin: 28px 0;
  padding: 26px 0;
  border-top: 1px solid ${colors.fender.grey02};
  border-bottom: 1px solid ${colors.fender.grey02};
`;

const FooterLink = styled.a`
  ${font('Palanquin', 500, '16px', 'normal', '.0225rem')}
  text-decoration: none;
  color: #fff;
  padding: 12px 0;
`;

interface MobileMenuDrawerProps {
  backButtonLabel?: string;
  brand: string;
  isOpen: boolean;
  items: ContentfulNavigationItem[];
  level: number;
  logoUrl?: string;
  onBackButtonClick?: () => void;
  onCloseButtonClick: () => void;
  onItemClick: (e: MouseEvent, item: ContentfulNavigationItem, level: number) => void;
  rootItem?: ContentfulNavigationItem;
  supportNav?: ContentfulNavigationItem[];
}

const MobileMenuDrawer: FC<MobileMenuDrawerProps> = ({
  backButtonLabel = 'Back',
  brand,
  isOpen,
  items,
  level,
  logoUrl,
  onBackButtonClick,
  onCloseButtonClick,
  onItemClick,
  rootItem,
  supportNav = []
}) => {
  const renderItems = [...items];
  let parentIsSupportNav = false;

  if (level == 2 && rootItem) {
    // Determine if the parent of this drawer is part of the support nav.
    if (supportNav) {
      const supportItemIndex = supportNav.findIndex(i => i.sys.id == rootItem.sys.id);
      if (supportItemIndex >= 0) {
        parentIsSupportNav = true;
      }
    }

    if (!parentIsSupportNav) {
      // The level 2 menu requires a Shop All link. Using and modifying the
      // parent Contentful Entry allows rendering in the loop with the rest. 
      const shopAllItem = {...rootItem, ...{
        fields: {
          title: 'Shop All',
          url: rootItem.fields.url,
          openInNewWindow: false
        }
      }};
  
      if (brand == 'jackson') {
        shopAllItem.fields.title = `Shop All ${stripHTML(rootItem.fields.title)}`;
      }
  
      renderItems.unshift(shopAllItem);
    }
  }

  return (
    <Drawer $brand={brand} $isOpen={isOpen}>
      <DrawerHeader $brand={brand} $level={level}>
        {(logoUrl) && (
          <LogoLink href="/" $brand={brand}>
            <img src={logoUrl} alt={`${brand} logo`} />
          </LogoLink>
        )}
        {(onBackButtonClick) && (
          <BackButton onClick={onBackButtonClick}>
            <span>
              <PointerLeft 
                fontSize="16px" 
                fill={(brand == 'jackson') ? '#fff' : '#000'} 
              />
            </span>
            {(brand == 'jackson') && backButtonLabel}
          </BackButton>
        )}
        {(brand == 'fender' && level > 1 && rootItem) && (
          <DrawerTitle
            // @ts-ignore, If the root is a ContentfulNavigationRow, use dedicated row title field.
            dangerouslySetInnerHTML={{__html: (rootItem.fields?.navRowTitle) ? rootItem.fields.navRowTitle : rootItem.fields.title}}
          />
        )}
        <CloseButton onClick={onCloseButtonClick}>
          <X fill={(brand == 'jackson') ? '#fff' : '#000'} fontSize="16px" />
        </CloseButton>
      </DrawerHeader>
      {/* Jackson displays parent title past level 1 */}
      {(brand == 'jackson' && level > 1 && rootItem) && (
        <SubMenuHeading className="level-2-wrapper">{stripHTML(rootItem.fields.title)}</SubMenuHeading>
      )}
      {/* Render parent assets as full width hero images at level 2*/}
      {(level ==2 && rootItem && rootItem.fields.asset) && (
        <img src={`https:${rootItem.fields.asset.fields.file?.url}`} />
      )}
      <Menu className={`level-${level}`} $brand={brand}>
        {/* These are the main navigation items from Contentful */}
        {renderItems.map((item, index) => (
          <li key={item.sys.id}>
            <MobileMenuLink 
              {...item} 
              brand={brand} 
              index={index}
              level={level}
              isSupportNav={parentIsSupportNav}
              onClick={e => onItemClick(e, item, level)} 
            />
          </li>
        ))}
        {/* Support navigation is a secondary menu, also Contentful */}
        {(level == 1 && supportNav.length > 0) && (
          <SupportNav>
            {supportNav.map((item, index) => (
              <li key={item.sys.id}>
                <MobileMenuLink
                  {...item}
                  isSupportNav
                  brand={brand}
                  index={index}
                  level={level}
                  onClick={e => onItemClick(e, item, level)}
                />
              </li>
            ))}
          </SupportNav>
        )}
        {/* System navigation is set of fixed links, defined in the Component. */}
        {(level == 1) && <MobileSystemNav brand={brand} />}
        {/* Jackson has an additional support link that sits alone at the bottom. */}
        {(level == 1 && brand == 'jackson') && (
          <FooterLink href="https://support.jacksonguitars.com" target="_blank">Support</FooterLink>
        )}
      </Menu>
    </Drawer>
  );
}

export default MobileMenuDrawer;